import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MousePointerClick, TrendingDown, Star } from "lucide-react";

const reasons = [
  {
    icon: MousePointerClick,
    title: "Higher Click-Through Rate",
    description:
      "Ads with a clear value proposition get 2-3x more clicks. When searchers instantly understand why you're different, they click.",
    stat: "2-3x",
    statLabel: "more clicks",
  },
  {
    icon: TrendingDown,
    title: "Lower Cost Per Click",
    description:
      "Google rewards relevant, compelling ad copy with a higher Quality Score — which directly reduces what you pay per click.",
    stat: "Up to 50%",
    statLabel: "lower CPC",
  },
  {
    icon: Star,
    title: "Better Quality Score",
    description:
      "A strong value proposition improves ad relevance and expected CTR — two of the three factors that determine your Quality Score.",
    stat: "7-10",
    statLabel: "Quality Score",
  },
];

const VPGWhySection = () => {
  return (
    <section className="py-24 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
              Why It Matters
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Your Value Proposition Is Your Ad's Secret Weapon
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The difference between an ad that gets ignored and one that gets
              clicked is a clear, compelling value proposition.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <Card className="h-full border-border hover:border-primary/30 transition-colors hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                    <reason.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-primary">
                      {reason.stat}
                    </span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {reason.statLabel}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {reason.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VPGWhySection;
